import React, {
  useRef,
  useEffect,
  memo,
  useMemo,
  useCallback,
  useState,
} from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Event } from "../../types/events";
import { Resource } from "../../types/resources";
import {
  ResourceGroup,
  SchedulerTheme,
  ViewInteractions,
  ViewConfig,
} from "../../types/views";
import ResourceHeader from "./ResourceHeader";
import ResourceList from "./ResourceList";
import TimelineHeader from "./TimelineHeader";
import GridContainer from "./GridContainer";
import { startOfDay, addDays, endOfDay, differenceInDays } from "date-fns";
import {
  TIMELINE_BASE_DAY_WIDTH,
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_RESOURCE_COLUMN_WIDTH,
} from "./timelineConstants";
import {
  createTimelineDragEndHandler,
  createTimelineSnapModifier,
} from "./timelineDnd";
import { buildTimelineRows } from "./timelineRows";
import { buildResourceLaneMap } from "./timelineLanes";
import { computeTimelineScrollMetrics } from "./timelineScrollToEvent";
import { TimelineScrollTarget } from "../../types/views";

export interface TimelineViewProps {
  events: Event[];
  resources: Resource[];
  getEventResourceIds?: (event: Event) => (string | number)[];
  currentDate: Date;
  daysToShow?: number;
  onEventClick?: ViewInteractions["onEventClick"];
  onTimeSlotClick?: ViewInteractions["onTimeSlotClick"];
  onResourceClick?: (resource: Resource) => void;
  zoom?: number;
  resourceHeaderContent?: React.ReactNode;
  selectedDateRange?: {
    start: Date;
    end: Date;
  };
  showTooltip?: boolean;
  viewConfig?: ViewConfig;
  onEventDrop?: (
    eventId: string,
    newStartTime: Date,
    newResource?: Resource
  ) => void | Promise<any>;
  isDraggable?: boolean;
  schedulerTheme?: SchedulerTheme;
  resourceGroups?: ResourceGroup[];
  scrollTarget?: TimelineScrollTarget | null;
  highlightedEventId?: string | null;
}

const TimelineView: React.FC<TimelineViewProps> = memo(
  ({
    events,
    resources,
    getEventResourceIds,
    currentDate,
    daysToShow = 7,
    onEventClick,
    onTimeSlotClick,
    onResourceClick,
    zoom = 1,
    resourceHeaderContent,
    selectedDateRange,
    showTooltip = false,
    viewConfig,
    onEventDrop,
    isDraggable = false,
    schedulerTheme,
    resourceGroups,
    scrollTarget = null,
    highlightedEventId = null,
  }) => {
    const timelineBodyRef = useRef<HTMLDivElement>(null);
    const resourceListRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const hasAutoScrolledToNowRef = useRef(false);
    const [headerTrailingSpacerWidth, setHeaderTrailingSpacerWidth] = useState(0);

    // Track previous values to determine if we need to reset scroll positions
    const prevDateRef = useRef<Date>(currentDate);
    const prevDateRangeRef = useRef<{ start: Date; end: Date } | undefined>(
      selectedDateRange
    );

    const initialViewStartDate = useMemo(
      () =>
        startOfDay(selectedDateRange ? selectedDateRange.start : currentDate),
      [currentDate, selectedDateRange]
    );

    const initialViewEndDate = useMemo(
      () =>
        selectedDateRange
          ? endOfDay(selectedDateRange.end)
          : endOfDay(addDays(initialViewStartDate, daysToShow - 1)),
      [initialViewStartDate, daysToShow, selectedDateRange]
    );

    // Include any event that overlaps the visible range, not only those starting within it
    const eventsOverlappingView = useMemo(
      () =>
        events.filter((event) => {
          const eventStart = new Date(event.timeRange.start);
          const eventEnd = new Date(event.timeRange.end);
          // Overlap condition: starts before view end AND ends after view start
          return (
            eventStart < initialViewEndDate && eventEnd > initialViewStartDate
          );
        }),
      [events, initialViewStartDate, initialViewEndDate]
    );

    const maxEndDate = useMemo(() => {
      // Start with the initial view end date as the minimum
      let maxDate = initialViewEndDate;

      // If there are events overlapping the view, check if any extend beyond the initial end date
      if (eventsOverlappingView.length > 0) {
        const lastEventEnd = eventsOverlappingView.reduce((max, event) => {
          const end = new Date(event.timeRange.end);
          return end > max ? end : max;
        }, new Date(0));

        // Use the later of the last event end or the initial view end date
        maxDate =
          lastEventEnd > initialViewEndDate ? lastEventEnd : initialViewEndDate;
      }

      // Always ensure we show at least the selected date range
      if (selectedDateRange && selectedDateRange.end > maxDate) {
        maxDate = endOfDay(selectedDateRange.end);
      }

      return maxDate;
    }, [eventsOverlappingView, initialViewEndDate, selectedDateRange]);

    const finalDaysToShow = useMemo(() => {
      // +1 because differenceInDays is exclusive of the last day
      return differenceInDays(maxEndDate, initialViewStartDate) + 1;
    }, [maxEndDate, initialViewStartDate]);

    // Lane assignments are computed once per render of the visible event set
    // and shared between row sizing (so rows expand to fit stacked events) and
    // the grid renderer (so each event gets the correct lane index).
    const resolveEventResources = useMemo(
      () =>
        getEventResourceIds ?? ((event: Event) => event.assignedResources || []),
      [getEventResourceIds]
    );

    const resourceLaneMap = useMemo(
      () =>
        buildResourceLaneMap(
          resources,
          eventsOverlappingView,
          resolveEventResources
        ),
      [resources, eventsOverlappingView, resolveEventResources]
    );

    const timelineRows = useMemo(
      () => buildTimelineRows(resources, resourceGroups, resourceLaneMap),
      [resourceGroups, resources, resourceLaneMap]
    );

    const syncHorizontalScroll = useCallback((scrollLeft: number) => {
      if (timelineBodyRef.current) {
        timelineBodyRef.current.scrollLeft = scrollLeft;
      }

      if (headerRef.current) {
        headerRef.current.scrollLeft = scrollLeft;
      }
    }, []);

    const getClampedScrollLeft = useCallback(
      (scrollLeft: number) => {
        const timelineBody = timelineBodyRef.current;

        if (!timelineBody) {
          return scrollLeft;
        }

        const viewportWidth = timelineBody.clientWidth;
        const contentWidth = finalDaysToShow * TIMELINE_BASE_DAY_WIDTH * zoom;
        const maxScrollLeft = Math.max(0, contentWidth - viewportWidth);

        return Math.min(Math.max(scrollLeft, 0), maxScrollLeft);
      },
      [finalDaysToShow, zoom]
    );

    useEffect(() => {
      const timelineBody = timelineBodyRef.current;
      if (!timelineBody) return;

      const measureScrollbarCompensation = () => {
        const nextWidth = Math.max(0, timelineBody.offsetWidth - timelineBody.clientWidth);
        setHeaderTrailingSpacerWidth((currentWidth) =>
          currentWidth === nextWidth ? currentWidth : nextWidth
        );
      };

      measureScrollbarCompensation();

      if (typeof ResizeObserver === "undefined") {
        window.addEventListener("resize", measureScrollbarCompensation);
        return () => {
          window.removeEventListener("resize", measureScrollbarCompensation);
        };
      }

      const resizeObserver = new ResizeObserver(measureScrollbarCompensation);
      resizeObserver.observe(timelineBody);
      return () => resizeObserver.disconnect();
    }, [timelineRows.totalHeight, finalDaysToShow, zoom]);

    useEffect(() => {
      hasAutoScrolledToNowRef.current = false;
    }, [initialViewStartDate, finalDaysToShow]);

    useEffect(() => {
      if (hasAutoScrolledToNowRef.current || !timelineBodyRef.current) {
        return;
      }

      const now = new Date();
      const viewEndExclusive = addDays(initialViewStartDate, finalDaysToShow);

      if (now < initialViewStartDate || now >= viewEndExclusive) {
        hasAutoScrolledToNowRef.current = true;
        return;
      }

      const currentHour = new Date(now);
      currentHour.setMinutes(0, 0, 0);

      const minutesFromViewStart =
        (currentHour.getTime() - initialViewStartDate.getTime()) / (1000 * 60);
      const hourOffset =
        (minutesFromViewStart / (24 * 60)) * TIMELINE_BASE_DAY_WIDTH * zoom;
      const targetScrollLeft = getClampedScrollLeft(hourOffset);

      const frameId = requestAnimationFrame(() => {
        syncHorizontalScroll(targetScrollLeft);
        hasAutoScrolledToNowRef.current = true;
      });

      return () => cancelAnimationFrame(frameId);
    }, [
      finalDaysToShow,
      getClampedScrollLeft,
      initialViewStartDate,
      syncHorizontalScroll,
      zoom,
    ]);

    useEffect(() => {
      if (!scrollTarget) {
        return;
      }

      let cancelled = false;
      let frameId = 0;

      const applyScroll = (attempt = 0) => {
        if (cancelled || !timelineBodyRef.current) {
          return;
        }

        const resourceRow = timelineRows.resourceRowMap.get(
          String(scrollTarget.resourceId),
        );

        if (!resourceRow) {
          if (attempt < 8) {
            frameId = requestAnimationFrame(() => applyScroll(attempt + 1));
          }
          return;
        }

        const timelineBody = timelineBodyRef.current;
        const { horizontalScrollLeft, verticalScrollTop } =
          computeTimelineScrollMetrics({
            eventStart: new Date(scrollTarget.eventStart),
            viewStartDate: initialViewStartDate,
            daysToShow: finalDaysToShow,
            zoom,
            resourceRowTop: resourceRow.top,
            resourceRowHeight: resourceRow.height,
            viewportWidth: timelineBody.clientWidth,
            viewportHeight: timelineBody.clientHeight,
            totalContentHeight: timelineRows.totalHeight,
          });

        frameId = requestAnimationFrame(() => {
          if (cancelled) {
            return;
          }

          syncHorizontalScroll(horizontalScrollLeft);
          timelineBody.scrollTop = verticalScrollTop;

          if (resourceListRef.current) {
            resourceListRef.current.scrollTop = verticalScrollTop;
          }
        });
      };

      applyScroll();

      return () => {
        cancelled = true;
        cancelAnimationFrame(frameId);
      };
    }, [
      finalDaysToShow,
      initialViewStartDate,
      scrollTarget,
      syncHorizontalScroll,
      timelineRows.resourceRowMap,
      timelineRows.totalHeight,
      zoom,
    ]);

    // Reset scroll positions only when there's a meaningful date navigation change
    useEffect(() => {
      const hasDateChanged =
        prevDateRef.current.getTime() !== currentDate.getTime();
      const hasDateRangeChanged =
        (!prevDateRangeRef.current && selectedDateRange) ||
        (prevDateRangeRef.current && !selectedDateRange) ||
        (prevDateRangeRef.current &&
          selectedDateRange &&
          (prevDateRangeRef.current.start.getTime() !==
            selectedDateRange.start.getTime() ||
            prevDateRangeRef.current.end.getTime() !==
              selectedDateRange.end.getTime()));

      // Only reset scroll positions if this is a real date navigation, not an internal state update
      if (hasDateChanged || hasDateRangeChanged) {
        // Only reset horizontal scroll, preserve vertical scroll position to avoid the first issue
        syncHorizontalScroll(0);
      }

      // Update refs for next comparison
      prevDateRef.current = currentDate;
      prevDateRangeRef.current = selectedDateRange;
    }, [currentDate, selectedDateRange, syncHorizontalScroll]);

    // Track previous scroll positions to detect scroll direction
    const prevScrollRef = useRef({ top: 0, left: 0 });

    // Sync vertical scrolling between resource list and timeline body
    const handleTimelineVerticalScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        if (
          resourceListRef.current &&
          resourceListRef.current.scrollTop !== e.currentTarget.scrollTop
        ) {
          resourceListRef.current.scrollTop = e.currentTarget.scrollTop;
        }
      },
      []
    );

    // Sync scrolling from resource list to timeline body
    const handleResourceScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        if (
          timelineBodyRef.current &&
          timelineBodyRef.current.scrollTop !== e.currentTarget.scrollTop
        ) {
          timelineBodyRef.current.scrollTop = e.currentTarget.scrollTop;
        }
      },
      []
    );

    // Sync horizontal scrolling between timeline body and header
    const handleTimelineHorizontalScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        if (
          headerRef.current &&
          headerRef.current.scrollLeft !== e.currentTarget.scrollLeft
        ) {
          headerRef.current.scrollLeft = e.currentTarget.scrollLeft;
        }
      },
      []
    );

    // Main scroll handler that differentiates between horizontal and vertical scrolling
    const handleScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        const currentScrollTop = e.currentTarget.scrollTop;
        const currentScrollLeft = e.currentTarget.scrollLeft;
        const prevScroll = prevScrollRef.current;

        // Only sync vertical scrolling if vertical position actually changed
        if (currentScrollTop !== prevScroll.top) {
          handleTimelineVerticalScroll(e);
        }

        // Only sync horizontal scrolling if horizontal position actually changed
        if (currentScrollLeft !== prevScroll.left) {
          handleTimelineHorizontalScroll(e);
        }

        // Update previous scroll position
        prevScrollRef.current = {
          top: currentScrollTop,
          left: currentScrollLeft,
        };
      },
      [handleTimelineVerticalScroll, handleTimelineHorizontalScroll]
    );

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: { distance: 8 },
      }),
      useSensor(TouchSensor),
      useSensor(KeyboardSensor)
    );

    const handleDragEnd = useMemo(
      () =>
        createTimelineDragEndHandler({
          zoom,
          getEventResourceIds,
          onEventDrop,
        }),
      [getEventResourceIds, onEventDrop, zoom]
    );

    const snapModifier = useMemo(() => createTimelineSnapModifier(zoom), [zoom]);

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={isDraggable ? [snapModifier] : []}
      >
        <div
          className="timeline-view"
          style={{
            display: "flex",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            position: "relative",
            backgroundColor: "white",
          }}
        >
          <div
            className="timeline-container"
            style={{
              width: `${TIMELINE_RESOURCE_COLUMN_WIDTH}px`,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              position: "relative",
              borderRight: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{
                flexShrink: 0,
                height: `${TIMELINE_HEADER_HEIGHT}px`,
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "white",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <ResourceHeader resourceHeaderContent={resourceHeaderContent} />
            </div>
            <div
              ref={resourceListRef}
              style={{
                flex: 1,
                overflow: "auto",
                position: "relative",
              }}
              onScroll={handleResourceScroll}
            >
              <ResourceList
                rows={timelineRows.rows}
                totalHeight={timelineRows.totalHeight}
                onResourceClick={onResourceClick}
              />
            </div>
          </div>

          <div
            className="timeline-content"
            style={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              height: "100%",
            }}
          >
            <TimelineHeader
              headerRef={headerRef}
              viewStartDate={initialViewStartDate}
              daysToShow={finalDaysToShow}
              zoom={zoom}
              BASE_DAY_WIDTH={TIMELINE_BASE_DAY_WIDTH}
              trailingSpacerWidth={headerTrailingSpacerWidth}
            />
            <div
              ref={timelineBodyRef}
              style={{
                flex: 1,
                overflow: "auto",
                position: "relative",
              }}
              onScroll={handleScroll}
            >
              <GridContainer
                events={eventsOverlappingView}
                resourceRows={timelineRows.resourceRows}
                resourceRowMap={timelineRows.resourceRowMap}
                resourceLaneMap={resourceLaneMap}
                totalHeight={timelineRows.totalHeight}
                getEventResourceIds={getEventResourceIds}
                startDate={initialViewStartDate}
                daysToShow={finalDaysToShow}
                zoom={zoom}
                baseDayWidth={TIMELINE_BASE_DAY_WIDTH}
                onEventClick={onEventClick}
                onTimeSlotClick={onTimeSlotClick}
                showTooltip={showTooltip}
                viewConfig={viewConfig}
                isDraggable={isDraggable}
                schedulerTheme={schedulerTheme}
                highlightedEventId={highlightedEventId}
              />
            </div>
          </div>
        </div>
      </DndContext>
    );
  }
);

export default TimelineView;
